import React, { useMemo, useState } from "react";
import { css, cx } from "@emotion/css";
import Button from "../../ui/button/button";
import { Modal } from "../modal/modal";
import { Input } from "../../ui/input/input";
import { Label } from "../../ui/label/label";
import PaymentMethodPix from "../../../features/checkout/components/payment-method-pix/payment-method-pix";
import PaymentMethodCreditCard from "../../../features/checkout/components/payment-method-credit-card/payment-method-credit-card";
import PaymentMethodDebitCard from "../../../features/checkout/components/payment-method-debit-card/payment-method-debit-card";
import { useAddBalance } from "../../../features/checkout/hooks/checkout-api.hooks";
import { useGetProfile } from "../../../features/profile/hooks/profile-api.hooks";
import {
  generateCardToken,
  getIssuerIdFromCardNumber,
  getPaymentMethodIdFromCardNumber,
} from "../../../features/checkout/utils/payment.utils";

const blackRoundButton = css({
  width: 40,
  height: 40,
  padding: 0,
  borderRadius: 999,
  background: "#000",
  border: "1px solid #000",
  color: "#fff",
  fontSize: 20,
  lineHeight: 1,
  ":hover": {
    background: "#000",
    borderColor: "#000",
    color: "#fff",
  },
  ":active": {
    transform: "translateY(1px)",
  },
});

const modalActions = css({
  display: "flex",
  gap: "var(--spacing-sm)",
  justifyContent: "flex-end",
  marginTop: "var(--spacing-xl)",
});

const methodsRow = css({
  display: "flex",
  gap: "var(--spacing-sm)",
  marginBottom: "var(--spacing-md)",
});

const methodPill = css({
  padding: "6px 10px",
  borderRadius: 999,
  border: "1px solid var(--g-300)",
  background: "var(--primary-color)",
  fontSize: "var(--fs-300)",
  cursor: "pointer",
  color: "var(--g-700)",
  transition: "all .15s ease",
  "&:hover": { borderColor: "var(--pc-500)", color: "var(--pc-700)" },
});

const methodPillActive = css({
  background: "rgba(var(--pcrgba-100), .4)",
  borderColor: "var(--pc-500)",
  color: "var(--pc-700)",
});

//

export interface BalanceAdditionButtonProps {
  className?: string;
}

const BalanceAdditionButton: React.FC<BalanceAdditionButtonProps> = ({
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState<"pix" | "credit" | "debit">("pix");
  const { mutateAsync, isPending, error } = useAddBalance();
  const [isCreditValid, setIsCreditValid] = useState(false);
  const [isDebitValid, setIsDebitValid] = useState(false);
  const [creditData, setCreditData] = useState<any | null>(null);
  const [debitData, setDebitData] = useState<any | null>(null);
  const [isTokenizing, setIsTokenizing] = useState(false);
  const [tokenError, setTokenError] = useState<string | null>(null);

  // Profile info to decide if we need to ask payer data
  const { data: profile } = useGetProfile();

  // Derive what exists on profile
  const profileCpf = profile?.cpf?.trim() || "";
  const profileName = profile?.name?.trim() || "";
  const nameParts = useMemo(
    () => profileName.split(/\s+/).filter(Boolean),
    [profileName]
  );
  const profileFirstName = nameParts[0] || "";
  const profileLastName = nameParts.slice(1).join(" ") || "";
  const profilePhone = profile?.phone?.trim() || "";

  const needCpf = !profileCpf;
  const needFirstName = !profileFirstName;
  const needLastName = !profileLastName;
  const needPhone = !profilePhone;

  // Inputs only shown if info is missing
  const [cpfInput, setCpfInput] = useState("");
  const [firstNameInput, setFirstNameInput] = useState("");
  const [lastNameInput, setLastNameInput] = useState("");
  const [phoneInput, setPhoneInput] = useState("");

  const normalizeCpf = (v: string) => v.replace(/\D/g, "").slice(0, 11);
  const normalizePhone = (v: string) => v.replace(/\D/g, "").slice(0, 13);
  const toE164BR = (raw: string) => {
    const digits = raw.replace(/\D/g, "");
    if (!digits) return "";
    if (digits.startsWith("55")) return `+${digits}`; // already with country code
    if (digits.length === 11) return `+55${digits}`; // local mobile (11)
    if (digits.length === 10) return `+55${digits}`; // local landline (10)
    return `+${digits}`; // last resort
  };

  const isValid = useMemo(() => {
    const v = Number((amount || "").replace(",", "."));
    const amountOk = Number.isFinite(v) && v > 0;

    // If any required payer data missing in profile, ensure inputs are valid
    const cpfOk = needCpf ? normalizeCpf(cpfInput).length === 11 : true;
    const firstOk = needFirstName ? firstNameInput.trim().length >= 2 : true;
    const lastOk = needLastName ? lastNameInput.trim().length >= 2 : true;
    const phoneOk = needPhone ? normalizePhone(phoneInput).length >= 10 : true;

    return amountOk && cpfOk && firstOk && lastOk && phoneOk;
  }, [
    amount,
    needCpf,
    needFirstName,
    needLastName,
    needPhone,
    cpfInput,
    firstNameInput,
    lastNameInput,
    phoneInput,
  ]);

  const amountNumber = useMemo(
    () => Number((amount || "").replace(",", ".")),
    [amount]
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid || isPending || isTokenizing) return;

    const value = Number((amount || "").replace(",", "."));
    const apiMethod =
      method === "pix"
        ? "pix"
        : method === "credit"
        ? "credit_card"
        : "debit_card";

    // Build payer only if anything is missing on profile
    const needsPayer = needCpf || needFirstName || needLastName || needPhone;
    const payer = needsPayer
      ? {
          cpf: profileCpf || normalizeCpf(cpfInput),
          firstName: profileFirstName || firstNameInput.trim(),
          lastName: profileLastName || lastNameInput.trim(),
          phoneNumber: toE164BR(profilePhone || phoneInput),
        }
      : undefined;

    try {
      setTokenError(null);
      if (apiMethod === "pix") {
        await mutateAsync({
          paymentMethod: apiMethod,
          transactionAmount: value,
          payer,
        });
      } else if (apiMethod === "credit_card") {
        if (!isCreditValid || !creditData) return;
        // Ensure token
        let cardToken = creditData.cardToken as string | undefined;
        if (!cardToken) {
          setIsTokenizing(true);
          const token = await generateCardToken({
            cardNumber: String(creditData.cardNumber || "").replace(/\D/g, ""),
            cardholderName: creditData.cardholderName,
            cardExpirationMonth: creditData.expirationMonth,
            cardExpirationYear:
              creditData.expirationYear.length === 2
                ? `20${creditData.expirationYear}`
                : creditData.expirationYear,
            securityCode: creditData.securityCode,
          });
          cardToken = token?.id;
          setIsTokenizing(false);
          if (!cardToken) throw new Error("Falha ao gerar token do cartão");
        }
        let issuerId: number | null = creditData.issuerId ?? null;
        if (!issuerId) {
          issuerId = await getIssuerIdFromCardNumber(
            creditData.cardNumber,
            "credit_card"
          );
        }
        const methodId = await getPaymentMethodIdFromCardNumber(
          creditData.cardNumber,
          "credit_card"
        );
        if (!methodId)
          throw new Error("Não foi possível identificar a bandeira do cartão");
        await mutateAsync({
          paymentMethod: methodId,
          transactionAmount: value,
          installments: Number(creditData.installments || 1),
          cardToken,
          issuerId: issuerId ?? undefined,
          payer,
        });
      } else if (apiMethod === "debit_card") {
        if (!isDebitValid || !debitData) return;
        // Tokenize debit as well (backend expects token for card methods)
        setIsTokenizing(true);
        const token = await generateCardToken({
          cardNumber: String(debitData.cardNumber || "").replace(/\D/g, ""),
          cardholderName: debitData.cardholderName,
          cardExpirationMonth: debitData.expirationMonth,
          cardExpirationYear:
            debitData.expirationYear.length === 2
              ? `20${debitData.expirationYear}`
              : debitData.expirationYear,
          securityCode: debitData.securityCode,
        });
        setIsTokenizing(false);
        const cardToken = token?.id as string | undefined;
        if (!cardToken) throw new Error("Falha ao gerar token do cartão");
        let issuerId: number | null = await getIssuerIdFromCardNumber(
          debitData.cardNumber,
          "debit_card"
        );
        const methodId = await getPaymentMethodIdFromCardNumber(
          debitData.cardNumber,
          "debit_card"
        );
        if (!methodId)
          throw new Error("Não foi possível identificar a bandeira do cartão");
        await mutateAsync({
          paymentMethod: methodId,
          transactionAmount: value,
          installments: 1,
          cardToken,
          issuerId: issuerId ?? undefined,
          payer,
        });
      }

      setAmount("");
      setIsOpen(false);
    } catch (err: any) {
      const msg = err?.message || "Erro ao processar pagamento";
      setTokenError(msg);
    } finally {
      setIsTokenizing(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        aria-label="Adicionar saldo"
        className={cx(blackRoundButton, className)}
        onClick={() => setIsOpen(true)}
      >
        +
      </Button>

      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title="Adicionar saldo"
      >
        <form onSubmit={handleSubmit}>
          <div className={methodsRow}>
            <button
              type="button"
              className={cx(methodPill, {
                [methodPillActive]: method === "pix",
              })}
              onClick={() => setMethod("pix")}
            >
              PIX
            </button>
            <button
              type="button"
              className={cx(methodPill, {
                [methodPillActive]: method === "credit",
              })}
              onClick={() => setMethod("credit")}
            >
              Crédito
            </button>
            <button
              type="button"
              className={cx(methodPill, {
                [methodPillActive]: method === "debit",
              })}
              onClick={() => setMethod("debit")}
            >
              Débito
            </button>
          </div>

          <div>
            <Label htmlFor="balance-amount" required>
              Valor (R$)
            </Label>
            <Input
              id="balance-amount"
              inputMode="decimal"
              type="text"
              placeholder="0,00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>

          {(needCpf || needFirstName || needLastName || needPhone) && (
            <div
              className={css({
                marginTop: "var(--spacing-lg)",
                paddingTop: "var(--spacing-md)",
                borderTop: "1px solid var(--g-200)",
                display: "grid",
                gap: "var(--spacing-md)",
              })}
            >
              <p
                className={css({
                  margin: 0,
                  color: "var(--g-700)",
                  fontWeight: 600,
                })}
              >
                Complete seus dados para continuar
              </p>

              {needCpf && (
                <div>
                  <Label htmlFor="payer-cpf" required>
                    CPF
                  </Label>
                  <Input
                    id="payer-cpf"
                    inputMode="numeric"
                    placeholder="000.000.000-00"
                    value={cpfInput}
                    onChange={(e) => setCpfInput(normalizeCpf(e.target.value))}
                  />
                </div>
              )}

              {needFirstName && (
                <div>
                  <Label htmlFor="payer-first" required>
                    Nome
                  </Label>
                  <Input
                    id="payer-first"
                    value={firstNameInput}
                    onChange={(e) => setFirstNameInput(e.target.value)}
                  />
                </div>
              )}

              {needLastName && (
                <div>
                  <Label htmlFor="payer-last" required>
                    Sobrenome
                  </Label>
                  <Input
                    id="payer-last"
                    value={lastNameInput}
                    onChange={(e) => setLastNameInput(e.target.value)}
                  />
                </div>
              )}

              {needPhone && (
                <div>
                  <Label htmlFor="payer-phone" required>
                    Celular
                  </Label>
                  <Input
                    id="payer-phone"
                    inputMode="tel"
                    placeholder="(11) 99999-9999"
                    value={phoneInput}
                    onChange={(e) =>
                      setPhoneInput(normalizePhone(e.target.value))
                    }
                  />
                </div>
              )}
            </div>
          )}

          {method === "pix" && (
            <div className={css({ marginTop: "var(--spacing-md)" })}>
              <PaymentMethodPix />
            </div>
          )}
          {method === "credit" && (
            <div className={css({ marginTop: "var(--spacing-md)" })}>
              <PaymentMethodCreditCard
                totalValue={amountNumber || 0}
                onDataChange={(data, valid) => {
                  setIsCreditValid(valid);
                  setCreditData(data);
                }}
              />
            </div>
          )}
          {method === "debit" && (
            <div className={css({ marginTop: "var(--spacing-md)" })}>
              <PaymentMethodDebitCard
                onDataChange={(data, valid) => {
                  setIsDebitValid(valid);
                  setDebitData(data);
                }}
              />
            </div>
          )}

          {(error || tokenError) && (
            <p
              className={css({
                color: "var(--error-color, #e53e3e)",
                marginTop: "var(--spacing-sm)",
              })}
            >
              {tokenError || "Ocorreu um erro ao adicionar saldo."}
            </p>
          )}

          <div className={modalActions}>
            <Button type="button" onClick={() => setIsOpen(false)}>
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={
                !isValid ||
                isPending ||
                isTokenizing ||
                (method === "credit" && !isCreditValid) ||
                (method === "debit" && !isDebitValid)
              }
              title={isTokenizing ? "Gerando token do cartão" : undefined}
            >
              {isPending || isTokenizing ? "Processando…" : "Adicionar"}
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
};

export default BalanceAdditionButton;
